import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Layout
import { AppLayout } from './components/layout/AppLayout';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import SetupOrganization from './pages/SetupOrganization';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Staff from './pages/Staff';
import ClockInOut from './pages/ClockInOut';
import Compliance from './pages/Compliance';
import DailyLogs from './pages/DailyLogs';
import Incidents from './pages/Incidents';
import Medications from './pages/Medications';
import Documents from './pages/Documents';
import Training from './pages/Training';
import HRSuite from './pages/HRSuite';
import QualityAssurance from './pages/QualityAssurance';
import AIAssistant from './pages/AIAssistant';
import ClientPortal from './pages/ClientPortal';
import CQCReadiness from './pages/CQCReadiness';
import Scheduling from './pages/Scheduling';
import NotificationSettings from './pages/NotificationSettings';

import './App.css';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />

      {/* Client Portal - separate public route */}
      <Route path="/client-portal" element={<ClientPortal />} />

      {/* Setup route (protected but outside main layout) */}
      <Route path="/setup-organization" element={
        <ProtectedRoute>
          <SetupOrganization />
        </ProtectedRoute>
      } />

      {/* Protected routes with layout */}
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="staff" element={<Staff />} />
        <Route path="clock" element={<ClockInOut />} />
        <Route path="scheduling" element={<Scheduling />} />
        <Route path="care-plans" element={<Clients />} />
        <Route path="daily-logs" element={<DailyLogs />} />
        <Route path="incidents" element={<Incidents />} />
        <Route path="medications" element={<Medications />} />
        <Route path="documents" element={<Documents />} />
        <Route path="training" element={<Training />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="hr-suite" element={<HRSuite />} />
        <Route path="quality-assurance" element={<QualityAssurance />} />
        <Route path="ai-assistant" element={<AIAssistant />} />
        <Route path="cqc-readiness" element={<CQCReadiness />} />
        <Route path="notification-settings" element={<NotificationSettings />} />
        <Route path="finance" element={<Dashboard />} />
        <Route path="settings" element={<Dashboard />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
